import React, { useState, useCallback, useRef } from "react";

import { QuizSection } from "../shared/QuizSection";
import {
  WeightQuestion,
  BodyConditionQuestion,
  ActivityQuestion,
  TreatFrequencyQuestion,
} from "../questions";
import { useRegisterSubSteps } from "@/state/SubStepContext";

type Dog = { id: string; form: any };

interface Props {
  dogs: Dog[];
  updateDog: (dogId: string, update: any) => void;
}

type SubStep = "weight" | "body" | "activity" | "treats";

const SUB_STEPS: SubStep[] = ["weight", "body", "activity", "treats"];

function isWeightComplete(form: any): boolean {
  return form?.weightKg !== "" && form?.weightKg != null && form.weightKg > 0;
}

export default function StepAbout({ dogs, updateDog }: Props) {
  const firstForm = dogs[0]?.form ?? {};

  const getFurthestSubStep = (): SubStep => {
    if (!dogs.every((d) => isWeightComplete(d.form))) return "weight";
    if (!dogs.every((d) => !!d.form?.bodyCondition)) return "body";
    if (!dogs.every((d) => !!d.form?.activityLevel)) return "activity";
    return "treats";
  };

  // Initial sub-step: saved value (on the first dog) if valid, else first incomplete.
  const getInitialSubStep = (): SubStep => {
    const saved = firstForm.subStepAbout as SubStep;
    if (saved && SUB_STEPS.includes(saved)) return saved;
    return getFurthestSubStep();
  };

  const [subStep, setSubStepLocal] = useState<SubStep>(getInitialSubStep);

  // Use refs to avoid stale closures in memoized callbacks
  const updateDogRef = useRef(updateDog);
  updateDogRef.current = updateDog;
  const firstDogIdRef = useRef(dogs[0]?.id);
  firstDogIdRef.current = dogs[0]?.id;
  const dogsRef = useRef(dogs);
  dogsRef.current = dogs;

  const handleStepClick = useCallback((stepId: string) => {
    setSubStepLocal(stepId as SubStep);
    if (firstDogIdRef.current) {
      updateDogRef.current(firstDogIdRef.current, { subStepAbout: stepId });
    }
  }, []);

  const canAdvanceSubStep = useCallback((step: string): boolean => {
    const all = dogsRef.current;
    switch (step) {
      case "weight":
        return all.every((d) => isWeightComplete(d.form));
      case "body":
        return all.every((d) => !!d.form?.bodyCondition);
      case "activity":
        return all.every((d) => !!d.form?.activityLevel);
      case "treats":
        return all.every((d) => !!d.form?.treatFrequency);
      default:
        return false;
    }
  }, []);

  useRegisterSubSteps(SUB_STEPS, subStep, handleStepClick, canAdvanceSubStep);

  return (
    <QuizSection>
      {subStep === "weight" &&
        dogs.map((dog) => (
          <WeightQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.weightKg as number | ""}
            onChange={(weight) => updateDog(dog.id, { weightKg: weight })}
          />
        ))}

      {subStep === "body" &&
        dogs.map((dog) => (
          <BodyConditionQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.bodyCondition}
            onChange={(condition) => updateDog(dog.id, { bodyCondition: condition })}
          />
        ))}

      {subStep === "activity" &&
        dogs.map((dog) => (
          <ActivityQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.activityLevel}
            onChange={(activity) => updateDog(dog.id, { activityLevel: activity })}
          />
        ))}

      {subStep === "treats" &&
        dogs.map((dog) => (
          <TreatFrequencyQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.treatFrequency}
            onChange={(frequency) => updateDog(dog.id, { treatFrequency: frequency })}
          />
        ))}
    </QuizSection>
  );
}
